import React from "react";
import { PaperPlaneRight, Broadcast, X, Paperclip } from "@phosphor-icons/react";

const ALLOWED_EXT = [".jpg", ".jpeg", ".png", ".webp", ".pdf", ".txt", ".csv", ".md"];
const MAX_FILES = 4;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

export default function ChatInputBar({
  input, setInput, mode, switchMode, files, setFiles,
  pendingSensor, onClearSensor, onOpenSensor,
  streaming, onSend, showToast, fileInputRef,
}) {
  const pickFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    const next = [...files];
    for (const f of chosen) {
      if (next.length >= MAX_FILES) { showToast(`Maximal ${MAX_FILES} Anhänge pro Anfrage.`, "error"); break; }
      if (f.size > MAX_FILE_BYTES) { showToast(`„${f.name}“ ist größer als 10 MB.`, "error"); continue; }
      const ok = ALLOWED_EXT.some((ext) => f.name.toLowerCase().endsWith(ext));
      if (!ok) { showToast(`Dateityp von „${f.name}“ wird nicht unterstützt.`, "error"); continue; }
      next.push(f);
    }
    setFiles(next);
  };

  const pendingSummary = pendingSensor
    ? pendingSensor.sensors.filter((s) => !s.error).map((s) => s.name.split(" /")[0]).join(", ")
    : "";

  return (
    <div className="border-t border-white/10 bg-bg px-3 pt-2 pb-3 space-y-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
      {pendingSensor && (
        <div data-testid="pending-sensor-chip" className="flex items-center justify-between bg-volt/15 border border-volt/40 rounded-xl px-3 py-2 text-xs">
          <div className="flex items-center gap-1.5 font-medium text-volthover">
            <Broadcast size={15} weight="bold" />
            <span>Messdaten bereit: {pendingSummary} ({pendingSensor.window.duration} s)</span>
          </div>
          <button data-testid="remove-sensor-chip" onClick={onClearSensor} className="p-1 text-muted hover:text-white">
            <X size={14} weight="bold" />
          </button>
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <span key={`${f.name}-${f.size}-${i}`} className="flex items-center gap-1 bg-elevated border border-white/10 rounded-full px-2.5 py-1 text-xs">
              <Paperclip size={12} />
              {f.name.length > 22 ? f.name.slice(0, 20) + "…" : f.name}
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-muted hover:text-white">
                <X size={12} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <div data-testid="tier-toggle" className="flex bg-elevated border border-white/10 rounded-full p-0.5 text-xs font-semibold">
          <button
            data-testid="tier-standard"
            onClick={() => switchMode("standard")}
            className={`px-3 py-1.5 rounded-full transition-colors ${mode === "standard" ? "bg-volt text-white" : "text-muted"}`}
          >Standard</button>
          <button
            data-testid="tier-pro"
            onClick={() => switchMode("pro")}
            className={`px-3 py-1.5 rounded-full transition-all ${mode === "pro" ? "bg-volt text-white shadow-[0_0_0_1px_rgba(34,197,94,0.85),0_0_14px_rgba(34,197,94,0.35)]" : "text-muted"}`}
          >Pro</button>
        </div>
        <button
          data-testid="add-environment-data-btn"
          onClick={onOpenSensor}
          className="flex items-center gap-1.5 text-xs font-semibold bg-elevated border border-white/10 rounded-full px-3 py-2 hover:border-volt/60 active:scale-95 transition-all"
        >
          <Broadcast size={15} weight="duotone" className="text-volt" />
          Umgebungsdaten
        </button>
      </div>
      <div className="flex items-end gap-2 min-w-0">
        {mode === "pro" && (
          <>
            <button
              data-testid="attach-file-btn"
              onClick={() => fileInputRef.current?.click()}
              className="shrink-0 p-3 rounded-full bg-elevated border border-white/10 hover:border-volt/60 active:scale-95 transition-all"
              title="Anhänge (max. 4, je 10 MB)"
            >
              <Paperclip size={18} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,.pdf,.txt,.csv,.md"
              className="hidden"
              onChange={pickFiles}
              data-testid="file-input"
            />
          </>
        )}
        <textarea
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          rows={1}
          placeholder={mode === "pro" ? "Pro-Frage stellen + Dateien" : "Frage stellen"}
          className="flex-1 min-w-0 w-0 bg-surface border border-white/10 rounded-2xl px-3 min-[375px]:px-4 py-3 text-sm min-[375px]:text-[15px] resize-none outline-none focus:border-volt/60 placeholder:text-muted/70 max-h-32"
        />
        <button
          data-testid="send-btn"
          onClick={onSend}
          disabled={streaming || !input.trim()}
          className="shrink-0 p-3 rounded-full bg-volt hover:bg-volthover disabled:opacity-40 disabled:pointer-events-none active:scale-95 transition-all"
        >
          <PaperPlaneRight size={18} weight="fill" />
        </button>
      </div>
    </div>
  );
}
